import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

// USBC Oil Pattern Conversion Formulas
const convertOilPatternAverage = (average: number, fromPattern: string, toPattern: string): number | null => {
  if (average <= 0 || fromPattern === toPattern) return null;
  
  const baseline = 110;
  const diff = average - baseline;
  
  // Normalize patterns to standard names
  const from = fromPattern.toLowerCase();
  const to = toPattern.toLowerCase();
  
  // Sport → Challenge: +9.1% easier
  if (from === 'sport' && to === 'challenge') {
    return Math.round(average + (diff * 0.091));
  }
  // Sport → Standard (House): +18.2% easier
  if (from === 'sport' && (to === 'standard' || to === 'house')) {
    return Math.round(average + (diff * 0.182));
  }
  // Challenge → Sport: -9.1% harder
  if (from === 'challenge' && to === 'sport') {
    return Math.round(average - (diff * 0.091));
  }
  // Challenge → Standard (House): +10% easier
  if (from === 'challenge' && (to === 'standard' || to === 'house')) {
    return Math.round(average + (diff * 0.1));
  }
  // Standard/House → Sport: -18.2% harder
  if ((from === 'standard' || from === 'house') && to === 'sport') {
    return Math.round(average - (diff * 0.182));
  }
  // Standard/House → Challenge: -10% harder
  if ((from === 'standard' || from === 'house') && to === 'challenge') {
    return Math.round(average - (diff * 0.1));
  }
  
  return null;
};

interface BowlerDetails {
  id: number;
  name: string;
  email: string;
  playerId: number;
  joinedAt: string;
  paidRegistrationFee: boolean;
  country: string;
  city: string;
  average: number;
  handicap: number;
  gender: string | null;
  dob: string | null;
}

interface LeagueRegistration {
  leagueId: number;
  leagueName: string;
  venue: string;
  city: string;
  startDate: string;
  endDate: string;
  teamId: number | null;
  teamName: string | null;
  registrationStatus: 'pending' | 'active' | 'cancelled' | 'completed';
  paymentComplete: boolean;
  registrationDate: string;
  division?: 'A' | 'B' | 'C' | 'D' | 'E' | null;
  handicapMaxes?: {
    a_max: number | null;
    b_max: number | null;
    c_max: number | null;
    d_max: number | null;
    e_max: number | null;
    league_max: number | null;
  };
  lockStartingHdcpForWeeks?: number | null;
  currentLeagueHandicap?: number | null;
  hasScoresInLeague?: boolean;
}

interface BowlerDetailData {
  bowler: BowlerDetails;
  registrations: LeagueRegistration[];
}

interface LeagueStat {
  leagueId: number;
  leagueName: string;
  leagueDescription?: string;
  average: number;
  handicap: number;
  gamesPlayed: number;
  weeklyAverage: number;
  weeklyHandicap: number;
  monthlyAverage: number;
  monthlyHandicap: number;
  yearlyAverage: number;
  yearlyHandicap: number;
  leagueAverage: number;
  leagueHandicap: number;
  lastCalculated: string;
}

interface BowlerStats {
  bowler: {
    id: number;
    name: string;
    lifetimeAverage: number;
    lifetimeHandicap: number;
  };
  globalAggregateStats: {
    weeklyAverage: number;
    weeklyHandicap: number;
    monthlyAverage: number;
    monthlyHandicap: number;
    yearlyAverage: number;
    yearlyHandicap: number;
    totalGamesPlayed: number;
  };
  adminAggregateStats: {
    weeklyAverage: number;
    weeklyHandicap: number;
    monthlyAverage: number;
    monthlyHandicap: number;
    yearlyAverage: number;
    yearlyHandicap: number;
    totalGamesPlayed: number;
  };
  filters: {
    timeframe: string;
    leagueId: string;
    gameRange: string;
  };
  adminLeagueStats: LeagueStat[];
  allLeagueStats: LeagueStat[];
}

interface AdminHandicapSettings {
  hdcp_base_average: number;
  hdcp_percentage: number;
  hdcp_max: number;
}

const BowlerDetail: React.FC = () => {
  const { bowlerId } = useParams<{ bowlerId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [bowlerData, setBowlerData] = useState<BowlerDetailData | null>(null);
  const [bowlerStats, setBowlerStats] = useState<BowlerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showHelpModal, setShowHelpModal] = useState(false);
  
  // State for league stats filtering
  const [selectedTimeframe, setSelectedTimeframe] = useState<'weekly' | 'monthly' | 'yearly' | 'per_league'>('per_league');
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('all');
  const [selectedGlobalTimeframe, setSelectedGlobalTimeframe] = useState<'weekly' | 'monthly' | 'yearly'>('yearly');
  const [selectedAdminTimeframe, setSelectedAdminTimeframe] = useState<'weekly' | 'monthly' | 'yearly'>('yearly');
  const [selectedAllLeaguesTimeframe, setSelectedAllLeaguesTimeframe] = useState<'weekly' | 'monthly' | 'yearly' | 'per_league'>('per_league');
  const [selectedAllLeagueId, setSelectedAllLeagueId] = useState<string>('all');
  const [statsLoading, setStatsLoading] = useState(false);
  
  // Oil Pattern Stats State
  const [oilPatternStats, setOilPatternStats] = useState<any>(null);
  const [oilPatternLoading, setOilPatternLoading] = useState(false);
  const [selectedOilLeagueScope, setSelectedOilLeagueScope] = useState<'my_leagues' | 'all_leagues'>('my_leagues');
  const [selectedOilPeriod, setSelectedOilPeriod] = useState<'all_time' | 'last_year' | 'last_month' | 'last_week'>('all_time');
  const [selectedOilPattern, setSelectedOilPattern] = useState<'all' | 'House' | 'Challenge' | 'Sport' | 'Not Set'>('all');
  const [oilBaseAverage, setOilBaseAverage] = useState<number>(200);
  const [oilHandicapPercentage, setOilHandicapPercentage] = useState<number>(80);
  
  // Custom handicap settings state
  const [adminSettings, setAdminSettings] = useState<AdminHandicapSettings>({
    hdcp_base_average: 200,
    hdcp_percentage: 80,
    hdcp_max: 40
  });
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  
  // DISABLED: Inline editing of average - can be re-enabled in the future
  // State for inline editing of average
  // const [isEditingAverage, setIsEditingAverage] = useState(false);
  // const [averageValue, setAverageValue] = useState<string>('');
  // const [originalAverage, setOriginalAverage] = useState<number>(0);
  
  // DISABLED: Inline editing of handicap - can be re-enabled in the future
  // State for inline editing of handicap
  // const [isEditingHandicap, setIsEditingHandicap] = useState(false);
  // const [handicapValue, setHandicapValue] = useState<string>('');
  // const [originalHandicap, setOriginalHandicap] = useState<number>(0);
  
  // State for remove player modal
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState<LeagueRegistration | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Gender & DOB editing
  const [infoGender, setInfoGender] = useState<string>('');
  const [infoDob, setInfoDob] = useState<string>('');
  const [savingInfo, setSavingInfo] = useState(false);
  const [infoMsg, setInfoMsg] = useState('');

  // State for division editing
  const [editingDivision, setEditingDivision] = useState<{ leagueId: number; division: string } | null>(null);
  const [savingDivision, setSavingDivision] = useState(false);

  // State for starting handicap editing
  const [editingStartingHdcp, setEditingStartingHdcp] = useState<{ leagueId: number; hdcp: string } | null>(null);
  const [savingStartingHdcp, setSavingStartingHdcp] = useState(false);

  const fetchBowlerDetails = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/bowlers/${bowlerId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBowlerData(data);
        setInfoGender(data.bowler.gender || '');
        setInfoDob(data.bowler.dob ? data.bowler.dob.split('T')[0] : '');
        // setOriginalAverage(data.bowler.average);
        // setAverageValue(data.bowler.average.toString());
        // setOriginalHandicap(data.bowler.handicap);
        // setHandicapValue(data.bowler.handicap.toString());
      } else if (response.status === 404) {
        setError('Bowler not found');
      } else {
        setError('Failed to fetch bowler details');
      }
    } catch (error) {
      console.error('Failed to fetch bowler details:', error);
      setError('Failed to fetch bowler details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminSettings = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin/handicap-settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAdminSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch admin settings:', error);
    }
  };

  const calculateCustomHandicap = (average: number): number => {
    const { hdcp_base_average, hdcp_percentage, hdcp_max } = adminSettings;
    const rawHandicap = Math.floor((hdcp_base_average - average) * (hdcp_percentage / 100));
    return Math.max(0, Math.min(rawHandicap, hdcp_max));
  };

  const updateAdminSettings = async (newSettings: AdminHandicapSettings) => {
    setSettingsLoading(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin/handicap-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newSettings)
      });

      if (response.ok) {
        const data = await response.json();
        setAdminSettings(data);
        setShowSettingsModal(false);
      } else {
        throw new Error('Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating admin settings:', error);
      alert('Failed to update settings. Please try again.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchBowlerStats = async () => {
    setStatsLoading(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const params = new URLSearchParams({
        timeframe: selectedTimeframe,
        leagueId: selectedLeagueId,
        gameRange: 'all'
      });
      
      const response = await fetch(`${apiUrl}/bowlers/${bowlerId}/stats?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBowlerStats(data);
      } else {
        console.error('Failed to fetch bowler stats');
      }
    } catch (error) {
      console.error('Failed to fetch bowler stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchOilPatternStats = async () => {
    setOilPatternLoading(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const params = new URLSearchParams({
        leagueScope: selectedOilLeagueScope,
        period: selectedOilPeriod,
        oilPattern: selectedOilPattern,
        baseAverage: oilBaseAverage.toString(),
        handicapPercentage: oilHandicapPercentage.toString()
      });
      
      const response = await fetch(`${apiUrl}/bowlers/${bowlerId}/oil-pattern-stats?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOilPatternStats(data);
      } else {
        console.error('Failed to fetch oil pattern stats');
      }
    } catch (error) {
      console.error('Failed to fetch oil pattern stats:', error);
    } finally {
      setOilPatternLoading(false);
    }
  };

  useEffect(() => {
    if (bowlerId) {
      fetchBowlerDetails();
      fetchBowlerStats();
      fetchAdminSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bowlerId]);

  // Fetch stats when filters change
  useEffect(() => {
    if (bowlerId && bowlerData) {
      fetchBowlerStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeframe, selectedLeagueId]);

  // Fetch oil pattern stats when filters change
  useEffect(() => {
    if (bowlerId && bowlerData) {
      fetchOilPatternStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOilLeagueScope, selectedOilPeriod, selectedOilPattern, oilBaseAverage, oilHandicapPercentage]);

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'cancelled':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'completed':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getDisplayedStats = () => {
    if (!bowlerStats || !bowlerStats.adminLeagueStats || bowlerStats.adminLeagueStats.length === 0) {
      return [];
    }

    // Filter by selected league if not 'all'
    const filteredStats = selectedLeagueId === 'all' 
      ? bowlerStats.adminLeagueStats 
      : bowlerStats.adminLeagueStats.filter((stat: LeagueStat) => stat.leagueId.toString() === selectedLeagueId);

    // Map to the correct timeframe values
    return filteredStats.map((stat: LeagueStat) => ({
      leagueId: stat.leagueId,
      leagueName: stat.leagueName,
      leagueDescription: stat.leagueDescription,
      average: selectedTimeframe === 'weekly' ? stat.weeklyAverage :
               selectedTimeframe === 'monthly' ? stat.monthlyAverage :
               selectedTimeframe === 'yearly' ? stat.yearlyAverage :
               stat.leagueAverage,
      handicap: selectedTimeframe === 'weekly' ? stat.weeklyHandicap :
                selectedTimeframe === 'monthly' ? stat.monthlyHandicap :
                selectedTimeframe === 'yearly' ? stat.yearlyHandicap :
                stat.leagueHandicap,
      gamesPlayed: stat.gamesPlayed,
      lastCalculated: stat.lastCalculated
    }));
  };

  const getDisplayedAllLeaguesStats = () => {
    if (!bowlerStats || !bowlerStats.allLeagueStats || bowlerStats.allLeagueStats.length === 0) {
      return [];
    }

    // Filter by selected league if not 'all'
    const filteredStats = selectedAllLeagueId === 'all' 
      ? bowlerStats.allLeagueStats 
      : bowlerStats.allLeagueStats.filter((stat: LeagueStat) => stat.leagueId.toString() === selectedAllLeagueId);

    // Map to the correct timeframe values
    return filteredStats.map((stat: LeagueStat) => ({
      leagueId: stat.leagueId,
      leagueName: stat.leagueName,
      leagueDescription: stat.leagueDescription,
      average: selectedAllLeaguesTimeframe === 'weekly' ? stat.weeklyAverage :
               selectedAllLeaguesTimeframe === 'monthly' ? stat.monthlyAverage :
               selectedAllLeaguesTimeframe === 'yearly' ? stat.yearlyAverage :
               stat.leagueAverage,
      handicap: selectedAllLeaguesTimeframe === 'weekly' ? stat.weeklyHandicap :
                selectedAllLeaguesTimeframe === 'monthly' ? stat.monthlyHandicap :
                selectedAllLeaguesTimeframe === 'yearly' ? stat.yearlyHandicap :
                stat.leagueHandicap,
      gamesPlayed: stat.gamesPlayed,
      lastCalculated: stat.lastCalculated
    }));
  };

  const handleGoToLeague = (leagueId: number) => {
    navigate(`/admin/leagues/${leagueId}/manage`);
  };

  // DISABLED: Average and Handicap editing functions - can be re-enabled in the future
  /*
  const handleStartEditingAverage = () => {
    setIsEditingAverage(true);
    setAverageValue(bowlerData?.bowler.average.toString() || '0');
    setOriginalAverage(bowlerData?.bowler.average || 0);
  };

  const handleSaveAverage = async () => {
    try {
      const newAverage = parseInt(averageValue);
      if (isNaN(newAverage) || newAverage < 0 || newAverage > 300) {
        alert('Average must be a valid number between 0 and 300');
        return;
      }

      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/bowlers/${bowlerId}/average`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ average: newAverage })
      });

      if (response.ok) {
        // Update the local state
        if (bowlerData) {
          setBowlerData({
            ...bowlerData,
            bowler: {
              ...bowlerData.bowler,
              average: newAverage
            }
          });
        }
        setIsEditingAverage(false);
        setOriginalAverage(newAverage);
      } else {
        throw new Error('Failed to update average');
      }
    } catch (error) {
      console.error('Error updating average:', error);
      alert('Failed to update average. Please try again.');
    }
  };

  const handleCancelEditingAverage = () => {
    setIsEditingAverage(false);
    setAverageValue(originalAverage.toString());
  };

  const handleAverageInputBlur = () => {
    // Do nothing on blur - only save/cancel buttons work
  };

  const handleStartEditingHandicap = () => {
    setIsEditingHandicap(true);
    setHandicapValue(bowlerData?.bowler.handicap.toString() || '0');
    setOriginalHandicap(bowlerData?.bowler.handicap || 0);
  };

  const handleSaveHandicap = async () => {
    try {
      const newHandicap = parseInt(handicapValue);
      if (isNaN(newHandicap) || newHandicap < 0 || newHandicap > 50) {
        alert('Handicap must be a valid number between 0 and 50');
        return;
      }

      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/bowlers/${bowlerId}/handicap`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ handicap: newHandicap })
      });

      if (response.ok) {
        // Update the local state
        if (bowlerData) {
          setBowlerData({
            ...bowlerData,
            bowler: {
              ...bowlerData.bowler,
              handicap: newHandicap
            }
          });
        }
        setIsEditingHandicap(false);
        setOriginalHandicap(newHandicap);
      } else {
        throw new Error('Failed to update handicap');
      }
    } catch (error) {
      console.error('Error updating handicap:', error);
      alert('Failed to update handicap. Please try again.');
    }
  };

  const handleCancelEditingHandicap = () => {
    setIsEditingHandicap(false);
    setHandicapValue(originalHandicap.toString());
  };

  const handleHandicapInputBlur = () => {
    // Do nothing on blur - only save/cancel buttons work
  };
  */

  const handleDivisionChange = (leagueId: number, newDivision: string) => {
    setEditingDivision({ leagueId, division: newDivision });
  };

  const handleSaveInfo = async () => {
    if (!bowlerData) return;
    setSavingInfo(true); setInfoMsg('');
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const res = await fetch(`${apiUrl}/bowlers/${bowlerData.bowler.id}/info`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ gender: infoGender || null, dob: infoDob || null }),
      });
      if (res.ok) {
        setInfoMsg('Saved!');
        setBowlerData(prev => prev ? { ...prev, bowler: { ...prev.bowler, gender: infoGender || null, dob: infoDob || null } } : prev);
        setTimeout(() => setInfoMsg(''), 3000);
      } else {
        setInfoMsg('Failed to save.');
      }
    } catch { setInfoMsg('Network error.'); }
    finally { setSavingInfo(false); }
  };

  const handleSaveDivision = async (leagueId: number) => {
    if (!editingDivision || editingDivision.leagueId !== leagueId) return;
    
    setSavingDivision(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const divisionValue = editingDivision.division === 'none' ? null : editingDivision.division;
      
      const response = await fetch(`${apiUrl}/bowlers/${bowlerId}/leagues/${leagueId}/division`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ division: divisionValue })
      });

      if (response.ok) {
        // Update local state
        if (bowlerData) {
          setBowlerData({
            ...bowlerData,
            registrations: bowlerData.registrations.map(reg =>
              reg.leagueId === leagueId
                ? { ...reg, division: divisionValue as any }
                : reg
            )
          });
        }
        setEditingDivision(null);
      } else {
        throw new Error('Failed to update division');
      }
    } catch (error) {
      console.error('Error updating division:', error);
      alert('Failed to update division. Please try again.');
    } finally {
      setSavingDivision(false);
    }
  };

  const handleCancelDivision = () => {
    setEditingDivision(null);
  };

  const handleStartingHdcpChange = (leagueId: number, newHdcp: string) => {
    // Only allow numbers
    if (newHdcp && !/^\d+$/.test(newHdcp)) return;
    setEditingStartingHdcp({ leagueId, hdcp: newHdcp });
  };

  const handleSaveStartingHdcp = async (leagueId: number) => {
    if (!editingStartingHdcp || editingStartingHdcp.leagueId !== leagueId) return;
    
    const hdcpValue = parseInt(editingStartingHdcp.hdcp);
    if (isNaN(hdcpValue) || hdcpValue < 0 || hdcpValue > 100) {
      alert('Handicap must be between 0 and 100');
      return;
    }
    
    setSavingStartingHdcp(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      
      const response = await fetch(`${apiUrl}/bowlers/${bowlerId}/leagues/${leagueId}/starting-handicap`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ startingHandicap: hdcpValue })
      });

      if (response.ok) {
        // Update local state
        if (bowlerData) {
          setBowlerData({
            ...bowlerData,
            registrations: bowlerData.registrations.map(reg =>
              reg.leagueId === leagueId
                ? { ...reg, currentLeagueHandicap: hdcpValue }
                : reg
            )
          });
        }
        setEditingStartingHdcp(null);
        // Refresh stats to show updated handicap
        fetchBowlerStats();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update starting handicap');
      }
    } catch (error: any) {
      console.error('Error updating starting handicap:', error);
      alert(error.message || 'Failed to update starting handicap. Please try again.');
    } finally {
      setSavingStartingHdcp(false);
    }
  };

  const handleCancelStartingHdcp = () => {
    setEditingStartingHdcp(null);
  };

  const handleRemovePlayerClick = (registration: LeagueRegistration) => {
    setSelectedRegistration(registration);
    setShowRemoveModal(true);
  };

  const handleConfirmRemovePlayer = async () => {
    if (!selectedRegistration) return;

    setIsRemoving(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const isAddingBack = selectedRegistration.registrationStatus === 'cancelled';
      const endpoint = isAddingBack ? 'addback' : 'remove';
      
      const response = await fetch(`${apiUrl}/players/${bowlerData?.bowler.playerId}/leagues/${selectedRegistration.leagueId}/${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        // Update the local state to reflect the new registration status
        if (bowlerData) {
          setBowlerData({
            ...bowlerData,
            registrations: bowlerData.registrations.map(reg =>
              reg.leagueId === selectedRegistration.leagueId
                ? { 
                    ...reg, 
                    registrationStatus: isAddingBack ? 'pending' as const : 'cancelled' as const 
                  }
                : reg
            )
          });
        }
        setShowRemoveModal(false);
        setSelectedRegistration(null);
      } else {
        throw new Error(`Failed to ${isAddingBack ? 'add player back to' : 'remove player from'} league`);
      }
    } catch (error) {
      console.error(`Error ${selectedRegistration.registrationStatus === 'cancelled' ? 'adding player back to' : 'removing player from'} league:`, error);
      alert(`Failed to ${selectedRegistration.registrationStatus === 'cancelled' ? 'add player back to' : 'remove player from'} league. Please try again.`);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleCancelRemovePlayer = () => {
    setShowRemoveModal(false);
    setSelectedRegistration(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">{t('admin.bowlerDetail.loadingDetails')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{error}</h2>
          <button
            onClick={() => navigate('/admin/bowlers/manage')}
            className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded"
          >
            {t('admin.bowlerDetail.backToBowlers')}
          </button>
        </div>
      </div>
    );
  }

  if (!bowlerData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/bowlers/manage')}
                className="text-white hover:text-purple-300 transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-3xl font-bold text-white">{t('admin.bowlerDetail.title')}</h1>
            </div>
          </div>
        </div>
      </div>

       {/* Help Card */}
      
        <div
          className="w-16 h-16 bg-yellow-600/30 rounded-full flex items-center justify-center shadow-lg cursor-pointer fixed bottom-8 right-8 z-50"
          onClick={() => setShowHelpModal(true)}
        >
          <span className="text-5xl text-yellow-300 font-bold">?</span>
        </div>
      

      {/* Help Modal */}
      {showHelpModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={e => {
            if (e.target === e.currentTarget) setShowHelpModal(false);
          }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-lg w-full mx-4 p-8 relative"
           style={{
              maxHeight: '80vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#6366f1 #18181b'
            }}>
            {/* Cool ? Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-600/30 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-5xl text-blue-300 font-bold">?</span>
              </div>
            </div>
            {/* Instructions Body */}
            <h2 className="text-2xl font-bold text-white text-center mb-4">{t('scout.bowlerDetail.helpTitle')}</h2>
            <div className="text-blue-200 text-base whitespace-pre-line">
              {t('scout.bowlerDetail.helpContent')}
            </div>
             <h2 className="text-2xl font-bold text-white text-center mt-2 mb-4">{t('admin.bowlerDetail.helpTitle')}</h2>
            <div className="text-blue-200 text-base whitespace-pre-line">
              {t('admin.bowlerDetail.helpContent')}
            </div>
            <button
              onClick={() => setShowHelpModal(false)}
              className="mt-8 w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Bowler Information Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-8">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-2xl">
                  {bowlerData.bowler.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{bowlerData.bowler.name}</h2>
                <p className="text-purple-200">{bowlerData.bowler.email}</p>
                {(bowlerData.bowler.city || bowlerData.bowler.country) && (
                  <p className="text-purple-300 text-sm mt-1">
                    📍 {[bowlerData.bowler.city, bowlerData.bowler.country].filter(Boolean).join(', ')}
                  </p>
                )}
                <div className="flex items-center space-x-4 mt-2 text-sm text-purple-300">
                  <span>{t('admin.bowlersManagement.bowlerId')}: {bowlerData.bowler.id}</span>
                </div>
                {/* Gender & DOB editing */}
                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <label className="text-purple-300 text-xs font-semibold">Gender:</label>
                    <select
                      value={infoGender}
                      onChange={e => setInfoGender(e.target.value)}
                      className="bg-gray-800 border border-white/20 text-white text-xs rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-400">
                      <option value="" className="bg-gray-800 text-white">— Not specified —</option>
                      <option value="M" className="bg-gray-800 text-white">Male</option>
                      <option value="F" className="bg-gray-800 text-white">Female</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-purple-300 text-xs font-semibold">DOB:</label>
                    <input
                      type="date"
                      value={infoDob}
                      onChange={e => setInfoDob(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className="bg-white/10 border border-white/20 text-white text-xs rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-400 [color-scheme:dark]"
                    />
                    {infoDob && (
                      <span className="text-purple-300 text-xs">
                        Age: {Math.floor((Date.now() - new Date(infoDob).getTime()) / (365.25 * 24 * 60 * 60 * 1000))}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={handleSaveInfo}
                    disabled={savingInfo}
                    className="px-3 py-1 bg-purple-700 hover:bg-purple-600 text-white text-xs font-semibold rounded-lg disabled:opacity-50 transition">
                    {savingInfo ? 'Saving...' : 'Save Info'}
                  </button>
                  {infoMsg && <span className="text-xs text-green-400">{infoMsg}</span>}
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-purple-200 text-sm">{t('admin.bowlerDetail.joined')}</p>
              <p className="text-white font-medium">
                {new Date(bowlerData.bowler.joinedAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          

          {/* Bowling Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column: Lifetime + Global Stats */}
            <div className="space-y-6">
              {/* Lifetime Stats Section - Side by Side */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Default Lifetime Stats - Read Only */}
                <div>
                  <h3 className="text-sm font-semibold text-white mb-2">{t('admin.bowlerDetail.lifetimeStats')}</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white/5 rounded-lg p-2 border border-white/10">
                      <div className="text-center">
                        <p className="text-purple-200 text-xs mb-1">{t('admin.bowlerDetail.avg')}</p>
                        <p className="text-lg font-bold text-white">
                          {bowlerStats?.bowler?.lifetimeAverage ?? bowlerData.bowler.average}
                        </p>
                      </div>
                    </div>
                    <div className="bg-white/5 rounded-lg p-2 border border-white/10">
                      <div className="text-center">
                        <p className="text-purple-200 text-xs mb-1">{t('admin.bowlerDetail.hdcp')}</p>
                        <p className="text-lg font-bold text-white">
                          {bowlerStats?.bowler?.lifetimeHandicap ?? bowlerData.bowler.handicap}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Custom Lifetime Stats */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-white">{t('admin.bowlerDetail.customLifetime')}</h3>
                    <button
                      onClick={() => setShowSettingsModal(true)}
                      className="text-purple-300 hover:text-purple-200 transition flex-shrink-0"
                      title="Edit custom handicap settings"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>
                  </div>
                  <div className="bg-purple-600/10 rounded-lg p-2 border border-purple-500/30">
                    <div className="text-xs text-purple-300 mb-1.5 text-center truncate">
                      {t('admin.bowlerDetail.base')}: {adminSettings.hdcp_base_average} | %: {adminSettings.hdcp_percentage} | {t('admin.bowlerDetail.max')}: {adminSettings.hdcp_max}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-white/5 rounded-lg p-2 border border-white/10">
                        <div className="text-center">
                          <p className="text-purple-200 text-xs mb-1">{t('admin.bowlerDetail.avg')}</p>
                          <p className="text-lg font-bold text-white">
                            {bowlerStats?.bowler?.lifetimeAverage ?? bowlerData.bowler.average}
                          </p>
                        </div>
                      </div>
                      <div className="bg-white/5 rounded-lg p-2 border border-white/10">
                        <div className="text-center">
                          <p className="text-purple-200 text-xs mb-1">{t('admin.bowlerDetail.hdcp')}</p>
                          <p className="text-lg font-bold text-white">
                            {calculateCustomHandicap(bowlerStats?.bowler?.lifetimeAverage ?? bowlerData.bowler.average)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Global Time-Based Stats Across All Leagues */}
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-3">
                  <h3 className="text-lg font-semibold text-white whitespace-nowrap">{t('admin.bowlerDetail.allLeaguesCombined')}</h3>
                  <select
                    value={selectedGlobalTimeframe}
                    onChange={(e) => setSelectedGlobalTimeframe(e.target.value as 'weekly' | 'monthly' | 'yearly')}
                    className="bg-purple-600/30 text-white text-sm rounded-lg px-3 py-2 border border-purple-500/50 focus:outline-none focus:border-purple-400 hover:bg-purple-600/40 transition cursor-pointer appearance-none w-full sm:w-auto"
                    style={{
                      backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.5rem center',
                      backgroundSize: '1.5em 1.5em',
                      paddingRight: '2.5rem'
                    }}
                  >
                    <option value="weekly" className="bg-purple-900 text-white">{t('admin.bowlerDetail.lastWeek')}</option>
                    <option value="monthly" className="bg-purple-900 text-white">{t('admin.bowlerDetail.lastMonth')}</option>
                    <option value="yearly" className="bg-purple-900 text-white">{t('admin.bowlerDetail.lastYear')}</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <div className="text-center">
                      <p className="text-purple-200 text-xs mb-1">{t('admin.bowlerDetail.average')}</p>
                      <p className="text-xl font-bold text-white">
                        {bowlerStats?.globalAggregateStats?.[
                          selectedGlobalTimeframe === 'weekly' ? 'weeklyAverage' :
                          selectedGlobalTimeframe === 'monthly' ? 'monthlyAverage' :
                          'yearlyAverage'
                        ] ?? 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <div className="text-center">
                      <p className="text-purple-200 text-xs mb-1">{t('admin.bowlerDetail.handicap')}</p>
                      <p className="text-xl font-bold text-white">
                        {bowlerStats?.globalAggregateStats?.[
                          selectedGlobalTimeframe === 'weekly' ? 'weeklyHandicap' :
                          selectedGlobalTimeframe === 'monthly' ? 'monthlyHandicap' :
                          'yearlyHandicap'
                        ] ?? 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* All Leagues Breakdown - Compact */}
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <h3 className="text-lg font-semibold text-white">{t('admin.bowlerDetail.allLeaguesBreakdown')}</h3>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select 
                      value={selectedAllLeaguesTimeframe}
                      onChange={(e) => setSelectedAllLeaguesTimeframe(e.target.value as 'weekly' | 'monthly' | 'yearly' | 'per_league')}
                      className="bg-purple-600/30 text-white text-xs rounded-lg px-2 py-1.5 border border-purple-500/50 focus:outline-none focus:border-purple-400 hover:bg-purple-600/40 transition cursor-pointer appearance-none w-full sm:w-auto"
                      style={{
                        backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.25rem center',
                        backgroundSize: '1em 1em',
                        paddingRight: '1.5rem'
                      }}
                    >
                      <option value="weekly" className="bg-purple-900 text-white">{t('admin.bowlerDetail.weekly')}</option>
                      <option value="monthly" className="bg-purple-900 text-white">{t('admin.bowlerDetail.monthly')}</option>
                      <option value="yearly" className="bg-purple-900 text-white">{t('admin.bowlerDetail.yearly')}</option>
                      <option value="per_league" className="bg-purple-900 text-white">{t('admin.bowlerDetail.perLeague')}</option>
                    </select>
                    <select 
                      value={selectedAllLeagueId}
                      onChange={(e) => setSelectedAllLeagueId(e.target.value)}
                      className="bg-purple-600/30 text-white text-xs rounded-lg px-2 py-1.5 border border-purple-500/50 focus:outline-none focus:border-purple-400 hover:bg-purple-600/40 transition cursor-pointer appearance-none w-full sm:w-auto"
                      style={{
                        backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.25rem center',
                        backgroundSize: '1em 1em',
                        paddingRight: '1.5rem'
                      }}
                    >
                      <option value="all" className="bg-purple-900 text-white">{t('admin.bowlerDetail.allLeagues')}</option>
                      {bowlerStats?.allLeagueStats?.map((stat: LeagueStat) => (
                        <option key={stat.leagueId} value={stat.leagueId} className="bg-purple-900 text-white">
                          {stat.leagueName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {statsLoading ? (
                  <div className="bg-white/5 rounded-lg p-6 border border-white/10 text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto mb-2"></div>
                    <p className="text-purple-200 text-xs">{t('admin.bowlerDetail.loadingStats')}</p>
                  </div>
                ) : getDisplayedAllLeaguesStats().length === 0 ? (
                  <div className="bg-white/5 rounded-lg p-6 border border-white/10 text-center">
                    <p className="text-purple-200 text-sm">{t('admin.bowlerDetail.noStatsAvailable')}</p>
                  </div>
                ) : (
                  <div 
                    className="space-y-2 max-h-[300px] overflow-y-auto pr-2"
                    style={{
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'rgba(147, 51, 234, 0.5) rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    {getDisplayedAllLeaguesStats().map((stat: any) => (
                      <div key={stat.leagueId} className={stat.leagueDescription === 'Side Action Only' ? 'bg-orange-500/10 rounded-lg p-3 border border-orange-400/40' : 'bg-white/5 rounded-lg p-3 border border-white/10'}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-white font-semibold text-sm">{stat.leagueName}</h4>
                          <span className={stat.leagueDescription === 'Side Action Only' ? 'text-orange-300 text-xs' : 'text-purple-300 text-xs'}>
                            {stat.leagueDescription === 'Side Action Only' ? `Side Action Only: ${stat.gamesPlayed}` : `${stat.gamesPlayed} ${stat.gamesPlayed === 1 ? 'game' : 'games'}`}
                          </span>
                        </div>
                        <div className="flex gap-2 items-center">
                          <div className="bg-white/5 rounded-lg p-2 border border-white/10 min-w-[75px]">
                            <p className="text-purple-200 text-xs mb-1 text-center">Avg</p>
                            <p className="text-lg font-bold text-white text-center">
                              {stat.average > 0 ? stat.average : 'N/A'}
                            </p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2 border border-white/10 min-w-[75px]">
                            <p className="text-purple-200 text-xs mb-1 text-center">Hdcp</p>
                            <p className="text-lg font-bold text-white text-center">
                              {stat.handicap}
                            </p>
                          </div>
                          <div className="flex-1 text-right">
                            <p className="text-purple-400 text-xs">
                              {new Date(stat.lastCalculated).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Per-League Stats with Filters */}
            <div className="space-y-6">
              {/* My Leagues Combined Stats */}
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-3">
                  <h3 className="text-lg font-semibold text-white whitespace-nowrap">{t('admin.bowlerDetail.myLeaguesCombined')}</h3>
                  <select
                    value={selectedAdminTimeframe}
                    onChange={(e) => setSelectedAdminTimeframe(e.target.value as 'weekly' | 'monthly' | 'yearly')}
                    className="bg-purple-600/30 text-white text-sm rounded-lg px-3 py-2 border border-purple-500/50 focus:outline-none focus:border-purple-400 hover:bg-purple-600/40 transition cursor-pointer appearance-none w-full sm:w-auto"
                    style={{
                      backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.5rem center',
                      backgroundSize: '1.5em 1.5em',
                      paddingRight: '2.5rem'
                    }}
                  >
                    <option value="weekly" className="bg-purple-900 text-white">{t('admin.bowlerDetail.lastWeek')}</option>
                    <option value="monthly" className="bg-purple-900 text-white">{t('admin.bowlerDetail.lastMonth')}</option>
                    <option value="yearly" className="bg-purple-900 text-white">{t('admin.bowlerDetail.lastYear')}</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <div className="text-center">
                      <p className="text-purple-200 text-xs mb-1">{t('admin.bowlerDetail.average')}</p>
                      <p className="text-xl font-bold text-white">
                        {bowlerStats?.adminAggregateStats?.[
                          selectedAdminTimeframe === 'weekly' ? 'weeklyAverage' :
                          selectedAdminTimeframe === 'monthly' ? 'monthlyAverage' :
                          'yearlyAverage'
                        ] ?? 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <div className="text-center">
                      <p className="text-purple-200 text-xs mb-1">{t('admin.bowlerDetail.handicap')}</p>
                      <p className="text-xl font-bold text-white">
                        {bowlerStats?.adminAggregateStats?.[
                          selectedAdminTimeframe === 'weekly' ? 'weeklyHandicap' :
                          selectedAdminTimeframe === 'monthly' ? 'monthlyHandicap' :
                          'yearlyHandicap'
                        ] ?? 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Per-League Breakdown */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                <h3 className="text-lg font-semibold text-white">{t('admin.bowlerDetail.myPerLeagueBreakdown')}</h3>
                <div className="flex flex-col sm:flex-row gap-2">
                  {/* Timeframe Filter */}
                  <select
                    value={selectedTimeframe}
                    onChange={(e) => setSelectedTimeframe(e.target.value as 'weekly' | 'monthly' | 'yearly' | 'per_league')}
                    className="bg-purple-600/30 text-white text-sm rounded-lg px-3 py-2 border border-purple-500/50 focus:outline-none focus:border-purple-400 hover:bg-purple-600/40 transition cursor-pointer appearance-none w-full sm:w-auto"
                    style={{
                      backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.5rem center',
                      backgroundSize: '1.5em 1.5em',
                      paddingRight: '2.5rem'
                    }}
                  >
                    <option value="weekly" className="bg-purple-900 text-white">{t('admin.bowlerDetail.lastWeek')}</option>
                    <option value="monthly" className="bg-purple-900 text-white">{t('admin.bowlerDetail.lastMonth')}</option>
                    <option value="yearly" className="bg-purple-900 text-white">{t('admin.bowlerDetail.lastYear')}</option>
                    <option value="per_league" className="bg-purple-900 text-white">{t('admin.bowlerDetail.allTime')}</option>
                  </select>

                  {/* League Filter */}
                  <select
                    value={selectedLeagueId}
                    onChange={(e) => setSelectedLeagueId(e.target.value)}
                    className="bg-purple-600/30 text-white text-sm rounded-lg px-3 py-2 border border-purple-500/50 focus:outline-none focus:border-purple-400 hover:bg-purple-600/40 transition cursor-pointer appearance-none w-full sm:w-auto"
                    style={{
                      backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.5rem center',
                      backgroundSize: '1.5em 1.5em',
                      paddingRight: '2.5rem'
                    }}
                  >
                    <option value="all" className="bg-purple-900 text-white">{t('admin.bowlerDetail.allLeagues')}</option>
                    {bowlerData.registrations.map(reg => (
                      <option key={reg.leagueId} value={reg.leagueId.toString()} className="bg-purple-900 text-white">
                        {reg.leagueName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Stats Display */}
              {statsLoading ? (
                <div className="bg-white/5 rounded-lg p-8 border border-white/10 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                  <p className="text-purple-200 text-sm">Loading stats...</p>
                </div>
              ) : getDisplayedStats().length === 0 ? (
                <div className="bg-white/5 rounded-lg p-8 border border-white/10 text-center">
                  <div className="text-3xl mb-2">📊</div>
                  <p className="text-purple-200">No league stats available</p>
                  <p className="text-purple-300 text-sm">Stats will appear once games are scored</p>
                </div>
              ) : (
                <div 
                  className="space-y-3 max-h-[400px] overflow-y-auto pr-2"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(147, 51, 234, 0.5) rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <style>{`
                    .space-y-3::-webkit-scrollbar {
                      width: 8px;
                    }
                    .space-y-3::-webkit-scrollbar-track {
                      background: rgba(255, 255, 255, 0.1);
                      border-radius: 4px;
                    }
                    .space-y-3::-webkit-scrollbar-thumb {
                      background: rgba(147, 51, 234, 0.5);
                      border-radius: 4px;
                    }
                    .space-y-3::-webkit-scrollbar-thumb:hover {
                      background: rgba(147, 51, 234, 0.7);
                    }
                  `}</style>
                  {getDisplayedStats().map(stat => (
                    <div key={stat.leagueId} className={stat.leagueDescription === 'Side Action Only' ? 'bg-orange-500/10 rounded-lg p-4 border border-orange-400/40' : 'bg-white/5 rounded-lg p-4 border border-white/10'}>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                        <h4 className="text-white font-semibold">{stat.leagueName}</h4>
                        <span className={stat.leagueDescription === 'Side Action Only' ? 'text-orange-300 text-sm' : 'text-purple-300 text-sm'}>
                          {stat.leagueDescription === 'Side Action Only' ? `Side Action Only: ${stat.gamesPlayed}` : `${stat.gamesPlayed} ${stat.gamesPlayed === 1 ? 'game' : 'games'}`}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                          <p className="text-purple-200 text-xs mb-1 text-center">Average</p>
                          <p className="text-xl font-bold text-white text-center">
                            {stat.average > 0 ? stat.average : 'N/A'}
                          </p>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                          <p className="text-purple-200 text-xs mb-1 text-center">Handicap</p>
                          <p className="text-xl font-bold text-white text-center">
                            {stat.handicap}
                          </p>
                        </div>
                        <div className="col-span-2 sm:col-span-1 text-center sm:text-right flex items-center justify-center sm:justify-end">
                          <p className="text-purple-400 text-xs">
                            Updated: {new Date(stat.lastCalculated).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats by Oil Pattern */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white">{t('admin.bowlerDetail.statsByOilPattern')}</h3>
          </div>
          
          {/* Filters Row 1 */}
          <div className="flex gap-3 mb-3 flex-wrap">
              {/* League Scope Filter */}
              <select
                value={selectedOilLeagueScope}
                onChange={(e) => setSelectedOilLeagueScope(e.target.value as 'my_leagues' | 'all_leagues')}
                className="bg-purple-600/30 text-white text-sm rounded-lg px-3 py-2 border border-purple-500/50 focus:outline-none focus:border-purple-400 hover:bg-purple-600/40 transition cursor-pointer appearance-none"
                style={{
                  backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem'
                }}
              >
                <option value="my_leagues" className="bg-purple-900 text-white">{t('admin.bowlerDetail.myLeagues')}</option>
                <option value="all_leagues" className="bg-purple-900 text-white">{t('admin.bowlerDetail.allLeagues')}</option>
              </select>

              {/* Period Filter */}
              <select
                value={selectedOilPeriod}
                onChange={(e) => setSelectedOilPeriod(e.target.value as 'all_time' | 'last_year' | 'last_month' | 'last_week')}
                className="bg-purple-600/30 text-white text-sm rounded-lg px-3 py-2 border border-purple-500/50 focus:outline-none focus:border-purple-400 hover:bg-purple-600/40 transition cursor-pointer appearance-none"
                style={{
                  backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem'
                }}
              >
                <option value="all_time" className="bg-purple-900 text-white">{t('admin.bowlerDetail.allTime')}</option>
                <option value="last_year" className="bg-purple-900 text-white">{t('admin.bowlerDetail.lastYear')}</option>
                <option value="last_month" className="bg-purple-900 text-white">{t('admin.bowlerDetail.lastMonth')}</option>
                <option value="last_week" className="bg-purple-900 text-white">{t('admin.bowlerDetail.lastWeek')}</option>
              </select>

              {/* Oil Pattern Filter */}
              <select
                value={selectedOilPattern}
                onChange={(e) => setSelectedOilPattern(e.target.value as 'all' | 'House' | 'Challenge' | 'Sport' | 'Not Set')}
                className="bg-purple-600/30 text-white text-sm rounded-lg px-3 py-2 border border-purple-500/50 focus:outline-none focus:border-purple-400 hover:bg-purple-600/40 transition cursor-pointer appearance-none"
                style={{
                  backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem'
                }}
              >
                <option value="all" className="bg-purple-900 text-white">{t('admin.bowlerDetail.allPatterns')}</option>
                <option value="House" className="bg-purple-900 text-white">House</option>
                <option value="Challenge" className="bg-purple-900 text-white">Challenge</option>
                <option value="Sport" className="bg-purple-900 text-white">Sport</option>
                <option value="Not Set" className="bg-purple-900 text-white">{t('admin.bowlerDetail.notSet')}</option>
              </select>
            </div>
          
          {/* Filters Row 2 - Handicap Settings */}
          <div className="flex gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-purple-200 text-sm whitespace-nowrap">Base Average:</label>
                <input
                  type="number"
                  min="100"
                  max="300"
                  value={oilBaseAverage}
                  onChange={(e) => setOilBaseAverage(parseInt(e.target.value) || 200)}
                  className="bg-purple-600/30 text-white text-sm rounded-lg px-3 py-2 border border-purple-500/50 focus:outline-none focus:border-purple-400 hover:bg-purple-600/40 transition w-20"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-purple-200 text-sm whitespace-nowrap">Hdcp %:</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={oilHandicapPercentage}
                  onChange={(e) => setOilHandicapPercentage(parseInt(e.target.value) || 80)}
                  className="bg-purple-600/30 text-white text-sm rounded-lg px-3 py-2 border border-purple-500/50 focus:outline-none focus:border-purple-400 hover:bg-purple-600/40 transition w-20"
                />
              </div>
            </div>

          {/* Cumulative Stats */}
          <div className="flex gap-3 mb-6 items-start">
            <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-[100px]">
              <div className="text-center">
                <p className="text-purple-200 text-xs mb-1">{t('admin.bowlerDetail.cumulativeAvg')}</p>
                <p className="text-xl font-bold text-white">
                  {oilPatternLoading ? '...' : oilPatternStats?.cumulativeAverage || 'N/A'}
                </p>
              </div>
            </div>
            <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-[100px]">
              <div className="text-center">
                <p className="text-purple-200 text-xs mb-1">{t('admin.bowlerDetail.cumulativeHdcp')}</p>
                <p className="text-xl font-bold text-white">
                  {oilPatternLoading ? '...' : oilPatternStats?.cumulativeHandicap || 'N/A'}
                </p>
              </div>
            </div>
            {/* Conversions for cumulative stats */}
            {!oilPatternLoading && oilPatternStats?.cumulativeAverage && selectedOilPattern !== 'all' && selectedOilPattern !== 'Not Set' && (
              <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                <p className="text-purple-200 text-xs mb-1">{t('admin.bowlerDetail.patternEquivalents')}</p>
                <div className="text-xs text-purple-300/80 space-y-1">
                  {selectedOilPattern.toLowerCase() === 'sport' && (
                    <>
                      <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'sport', 'challenge')} Challenge</div>
                      <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'sport', 'house')} House</div>
                    </>
                  )}
                  {selectedOilPattern.toLowerCase() === 'challenge' && (
                    <>
                      <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'challenge', 'sport')} Sport</div>
                      <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'challenge', 'house')} House</div>
                    </>
                  )}
                  {selectedOilPattern.toLowerCase() === 'house' && (
                    <>
                      <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'house', 'challenge')} Challenge</div>
                      <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'house', 'sport')} Sport</div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* League Breakdown */}
          {oilPatternLoading ? (
            <div className="bg-white/5 rounded-lg p-8 border border-white/10 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
              <p className="text-purple-200 text-sm">{t('admin.bowlerDetail.loadingStats')}</p>
            </div>
          ) : !oilPatternStats?.leagues || oilPatternStats.leagues.length === 0 ? (
            <div className="bg-white/5 rounded-lg p-8 border border-white/10 text-center">
              <div className="text-3xl mb-2">🎳</div>
              <p className="text-purple-200">{t('admin.bowlerDetail.noOilPatternData')}</p>
              <p className="text-purple-300 text-sm">{t('admin.bowlerDetail.noOilPatternDataDesc')}</p>
            </div>
          ) : (
            <div 
              className="space-y-3 max-h-[400px] overflow-y-auto pr-2"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(147, 51, 234, 0.5) rgba(255, 255, 255, 0.1)'
              }}
            >
              {oilPatternStats.leagues.map((league: any) => (
                <div key={`${league.leagueId}-${league.oilPattern}`} className={league.leagueDescription === 'Side Action Only' ? 'bg-orange-500/10 rounded-lg p-4 border border-orange-400/40' : 'bg-white/5 rounded-lg p-4 border border-white/10'}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-white font-semibold">{league.leagueName}</h4>
                      <p className="text-purple-300 text-xs">🎳 {league.oilPattern === 'Not Set' ? t('admin.bowlerDetail.notSet') : league.oilPattern}</p>
                    </div>
                    <span className={league.leagueDescription === 'Side Action Only' ? 'text-orange-300 text-sm' : 'text-purple-300 text-sm'}>
                      {league.leagueDescription === 'Side Action Only' ? `Side Action Only: ${league.gamesPlayed}` : `${league.gamesPlayed} ${league.gamesPlayed === 1 ? 'game' : 'games'}`}
                    </span>
                  </div>
                  <div className="flex gap-3 items-start">
                    <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-[90px]">
                      <p className="text-purple-200 text-xs mb-1 text-center">{t('admin.bowlerDetail.average')}</p>
                      <p className="text-xl font-bold text-white text-center">
                        {league.average > 0 ? league.average : 'N/A'}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-[90px]">
                      <p className="text-purple-200 text-xs mb-1 text-center">{t('admin.bowlerDetail.handicap')}</p>
                      <p className="text-xl font-bold text-white text-center">
                        {league.handicap}
                      </p>
                    </div>
                    {/* Show conversion hints for different patterns */}
                    {league.average > 0 && league.oilPattern !== 'Not Set' && (
                      <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                        <p className="text-purple-200 text-[10px] mb-1">{t('admin.bowlerDetail.equivalents')}</p>
                        <div className="text-[10px] text-purple-300/70 space-y-0.5">
                          {league.oilPattern.toLowerCase() === 'sport' && (
                            <>
                              <div>≈ {convertOilPatternAverage(Number(league.average), 'sport', 'challenge')} Challenge</div>
                              <div>≈ {convertOilPatternAverage(Number(league.average), 'sport', 'house')} House</div>
                            </>
                          )}
                          {league.oilPattern.toLowerCase() === 'challenge' && (
                            <>
                              <div>≈ {convertOilPatternAverage(Number(league.average), 'challenge', 'sport')} Sport</div>
                              <div>≈ {convertOilPatternAverage(Number(league.average), 'challenge', 'house')} House</div>
                            </>
                          )}
                          {(league.oilPattern.toLowerCase() === 'house' || league.oilPattern.toLowerCase() === 'standard') && (
                            <>
                              <div>≈ {convertOilPatternAverage(Number(league.average), 'house', 'challenge')} Challenge</div>
                              <div>≈ {convertOilPatternAverage(Number(league.average), 'house', 'sport')} Sport</div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Quick Stats */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-2 border border-white/20 mb-2">
          <h3 className="text-md font-bold text-white mb-1">{t('admin.bowlerDetail.quickStats')}</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white/5 rounded-lg p-2 border border-white/10">
              <div className="text-center">
                <p className="text-md font-bold text-white">{bowlerData.registrations.length}</p>
                <p className="text-purple-200 text-sm">{t('admin.bowlerDetail.leagueRegistrations')}</p>
              </div>
            </div>
            <div className="bg-white/5 rounded-lg p-2 border border-white/10">
              <div className="text-center">
                <p className="text-md font-bold text-white">
                  {bowlerData.registrations.filter(reg => reg.registrationStatus === 'active').length}
                </p>
                <p className="text-purple-200 text-sm">{t('admin.bowlerDetail.activeLeagues')}</p>
              </div>
            </div>
            <div className="bg-white/5 rounded-lg p-2 border border-white/10">
              <div className="text-center">
                <p className="text-md font-bold text-white">
                  {bowlerData.registrations.filter(reg => reg.teamName).length}
                </p>
                <p className="text-purple-200 text-sm">{t('admin.bowlerDetail.teamsJoined')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* League Registrations */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
          <h3 className="text-xl font-bold text-white mb-6">{t('admin.bowlerDetail.leagueRegistrations')}</h3>
          
          {bowlerData.registrations.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">🎳</div>
              <p className="text-purple-200">{t('admin.bowlerDetail.noBowlerRegistrations')}</p>
              <p className="text-purple-300 text-sm">{t('admin.bowlerDetail.notRegistered')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {bowlerData.registrations.map((registration, index) => (
                <div
                  key={index}
                  className="bg-white/5 rounded-lg p-4 border border-white/10 hover:bg-white/10 transition duration-200"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h4 className="text-lg font-semibold text-white">{registration.leagueName}</h4>
                        <span className={`px-2 py-1 rounded text-xs border ${getStatusBadgeColor(registration.registrationStatus)}`}>
                          {registration.registrationStatus}
                        </span>
                        {registration.paymentComplete ? (
                          <span className="bg-green-600/20 text-green-300 px-2 py-1 rounded text-xs border border-green-500/30">
                            {t('admin.bowlerDetail.paid')}
                          </span>
                        ) : (
                          <span className="bg-red-600/20 text-red-300 px-2 py-1 rounded text-xs border border-red-500/30">
                            {t('admin.bowlerDetail.notPaid')}
                          </span>
                        )}
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4 text-sm text-purple-300">
                        <div>
                          <p><strong className="text-white">{t('admin.bowlerDetail.venue')}:</strong> {registration.venue}</p>
                          <p><strong className="text-white">{t('admin.bowlerDetail.location')}:</strong> {registration.city}</p>
                          <p><strong className="text-white">{t('admin.bowlerDetail.team')}: <span className="text-blue-300">{registration.teamName || t('admin.bowlerDetail.noTeamAssigned')}</span></strong></p>
                        </div>
                        <div>
                          <p><strong className="text-white">{t('admin.bowlerDetail.startDate')}:</strong> {new Date(registration.startDate).toLocaleDateString()}</p>
                          <p><strong className="text-white">{t('admin.bowlerDetail.endDate')}:</strong> {new Date(registration.endDate).toLocaleDateString()}</p>
                          <p><strong className="text-white">{t('admin.bowlerDetail.registered')}:</strong> {new Date(registration.registrationDate).toLocaleDateString()}</p>
                        </div>
                      </div>

                      {/* Division Selection */}
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <div className="flex items-center gap-3">
                          <label className="text-white text-sm font-medium">{t('admin.bowlerDetail.division')}:</label>
                          <select
                            value={
                              editingDivision?.leagueId === registration.leagueId
                                ? editingDivision.division
                                : registration.division || 'none'
                            }
                            onChange={(e) => handleDivisionChange(registration.leagueId, e.target.value)}
                            className="bg-white/10 text-white text-sm rounded-lg px-3 py-1.5 border border-white/20 focus:outline-none focus:border-purple-400 hover:bg-white/15 transition"
                          >
                            <option value="none" className="bg-gray-800">
                              {t('admin.bowlerDetail.noDivision')} {registration.handicapMaxes?.league_max ? `(${t('admin.bowlerDetail.max')} ${registration.handicapMaxes.league_max})` : `(no ${t('admin.bowlerDetail.max')})`}
                            </option>
                            <option value="A" className="bg-gray-800">
                              {t('admin.bowlerDetail.divisionA')} {registration.handicapMaxes?.a_max ? `(${t('admin.bowlerDetail.max')} ${registration.handicapMaxes.a_max})` : `(no ${t('admin.bowlerDetail.max')})`}
                            </option>
                            <option value="B" className="bg-gray-800">
                              {t('admin.bowlerDetail.divisionB')} {registration.handicapMaxes?.b_max ? `(${t('admin.bowlerDetail.max')} ${registration.handicapMaxes.b_max})` : `(no ${t('admin.bowlerDetail.max')})`}
                            </option>
                            <option value="C" className="bg-gray-800">
                              {t('admin.bowlerDetail.divisionC')} {registration.handicapMaxes?.c_max ? `(${t('admin.bowlerDetail.max')} ${registration.handicapMaxes.c_max})` : `(no ${t('admin.bowlerDetail.max')})`}
                            </option>
                            <option value="D" className="bg-gray-800">
                              {t('admin.bowlerDetail.divisionD')} {registration.handicapMaxes?.d_max ? `(${t('admin.bowlerDetail.max')} ${registration.handicapMaxes.d_max})` : `(no ${t('admin.bowlerDetail.max')})`}
                            </option>
                            <option value="E" className="bg-gray-800">
                              {t('admin.bowlerDetail.divisionE')} {registration.handicapMaxes?.e_max ? `(${t('admin.bowlerDetail.max')} ${registration.handicapMaxes.e_max})` : `(no ${t('admin.bowlerDetail.max')})`}
                            </option>
                          </select>
                          
                          {editingDivision?.leagueId === registration.leagueId && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSaveDivision(registration.leagueId)}
                                disabled={savingDivision}
                                className="bg-green-600/20 hover:bg-green-600 text-green-300 hover:text-white p-1.5 rounded transition border border-green-600/30 disabled:opacity-50"
                                title="Save division"
                              >
                                {savingDivision ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-300"></div>
                                ) : (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </button>
                              <button
                                onClick={handleCancelDivision}
                                disabled={savingDivision}
                                className="bg-red-600/20 hover:bg-red-600 text-red-300 hover:text-white p-1.5 rounded transition border border-red-600/30 disabled:opacity-50"
                                title="Cancel"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Starting Locked Handicap - Only show if lock_starting_hdcp_for_weeks is configured */}
                      {registration.lockStartingHdcpForWeeks && registration.lockStartingHdcpForWeeks > 0 && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <div className="flex items-center gap-3">
                            <label className="text-white text-sm font-medium whitespace-nowrap">{t('admin.bowlerDetail.startingLockedHdcp')}:</label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={
                                editingStartingHdcp?.leagueId === registration.leagueId
                                  ? editingStartingHdcp.hdcp
                                  : registration.currentLeagueHandicap || ''
                              }
                              onChange={(e) => handleStartingHdcpChange(registration.leagueId, e.target.value)}
                              disabled={registration.hasScoresInLeague}
                              placeholder={registration.hasScoresInLeague ? 'Locked' : 'Enter hdcp'}
                              className={`bg-white/10 text-white text-sm rounded-lg px-3 py-1.5 border border-white/20 focus:outline-none focus:border-amber-400 hover:bg-white/15 transition w-24 ${
                                registration.hasScoresInLeague ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                              title={
                                registration.hasScoresInLeague
                                  ? 'First squad has been bowled (remove scores for first squad to set starting hdcp)'
                                  : 'Set starting handicap before first squad is bowled'
                              }
                            />
                            
                            {editingStartingHdcp?.leagueId === registration.leagueId && !registration.hasScoresInLeague && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleSaveStartingHdcp(registration.leagueId)}
                                  disabled={savingStartingHdcp}
                                  className="bg-green-600/20 hover:bg-green-600 text-green-300 hover:text-white p-1.5 rounded transition border border-green-600/30 disabled:opacity-50"
                                  title="Save starting handicap"
                                >
                                  {savingStartingHdcp ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-300"></div>
                                  ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </button>
                                <button
                                  onClick={handleCancelStartingHdcp}
                                  disabled={savingStartingHdcp}
                                  className="bg-red-600/20 hover:bg-red-600 text-red-300 hover:text-white p-1.5 rounded transition border border-red-600/30 disabled:opacity-50"
                                  title="Cancel"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            )}
                            
                            {registration.lockStartingHdcpForWeeks && (
                              <span className="text-xs text-blue-300 italic">
                                (Locked for {registration.lockStartingHdcpForWeeks} weeks)
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex sm:flex-col gap-2 sm:min-w-[140px]">
                      <button
                        onClick={() => handleGoToLeague(registration.leagueId)}
                        className="bg-purple-600/20 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition duration-200 flex items-center justify-center space-x-2 border border-purple-600 whitespace-nowrap"
                      >
                        <span>{t('admin.bowlerDetail.goToLeague')}</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                      {/* Remove/Add Back Button */}
                      {registration.registrationStatus === 'cancelled' ? (
                        <button
                          onClick={() => handleRemovePlayerClick(registration)}
                          className="bg-green-600/20 text-xs hover:bg-green-600 text-white px-3 py-1.5 rounded-lg transition duration-200 flex items-center justify-center space-x-2 border border-green-600/30 whitespace-nowrap"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          <span>{t('admin.bowlerDetail.addPlayerBack')}</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRemovePlayerClick(registration)}
                          className="bg-red-600/20 text-xs hover:bg-red-600 text-white px-3 py-1.5 rounded-lg transition duration-200 flex items-center justify-center space-x-2 border border-red-600/30 whitespace-nowrap"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span>{t('admin.bowlerDetail.removeFromLeague')}</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Player Action Confirmation Modal */}
      {showRemoveModal && selectedRegistration && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 max-w-md w-full mx-4">
            <div className="text-center">
              <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4 ${
                selectedRegistration.registrationStatus === 'cancelled' 
                  ? 'bg-green-100/10' 
                  : 'bg-red-100/10'
              }`}>
                {selectedRegistration.registrationStatus === 'cancelled' ? (
                  <svg className="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                )}
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                {selectedRegistration.registrationStatus === 'cancelled' 
                  ? t('admin.bowlerDetail.addPlayerBackTitle')
                  : t('admin.bowlerDetail.removePlayerTitle')
                }
              </h3>
              <p className="text-purple-200 text-sm mb-6"
                dangerouslySetInnerHTML={{
                  __html: selectedRegistration.registrationStatus === 'cancelled'
                    ? t('admin.bowlerDetail.addPlayerBackMessage', { name: bowlerData?.bowler.name, league: selectedRegistration.leagueName })
                    : t('admin.bowlerDetail.removePlayerMessage', { name: bowlerData?.bowler.name, league: selectedRegistration.leagueName })
                }}
              />
              <div className="flex space-x-4">
                <button
                  onClick={handleCancelRemovePlayer}
                  disabled={isRemoving}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition duration-200 disabled:opacity-50"
                >
                  {t('admin.bowlerDetail.cancel')}
                </button>
                <button
                  onClick={handleConfirmRemovePlayer}
                  disabled={isRemoving}
                  className={`flex-1 px-4 py-2 rounded-lg transition duration-200 disabled:opacity-50 flex items-center justify-center ${
                    selectedRegistration.registrationStatus === 'cancelled'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  }`}
                >
                  {isRemoving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {selectedRegistration.registrationStatus === 'cancelled' ? t('admin.bowlerDetail.adding') : t('admin.bowlerDetail.removing')}
                    </>
                  ) : (
                    selectedRegistration.registrationStatus === 'cancelled' ? t('admin.bowlerDetail.addPlayerBack') : t('admin.bowlerDetail.removePlayer')
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Handicap Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">{t('admin.bowlerDetail.customHandicapSettings')}</h3>
            <p className="text-purple-200 text-sm mb-6">
              {t('admin.bowlerDetail.configureHandicap')}
            </p>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  {t('admin.bowlerDetail.baseAverage')}
                </label>
                <input
                  type="number"
                  value={adminSettings.hdcp_base_average}
                  onChange={(e) => setAdminSettings({ ...adminSettings, hdcp_base_average: parseInt(e.target.value) || 0 })}
                  className="w-full bg-white/10 text-white rounded-lg px-4 py-2 border border-white/20 focus:outline-none focus:border-purple-400"
                  min="0"
                  max="300"
                />
              </div>
              
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  {t('admin.bowlerDetail.handicapPercentage')}
                </label>
                <input
                  type="number"
                  value={adminSettings.hdcp_percentage}
                  onChange={(e) => setAdminSettings({ ...adminSettings, hdcp_percentage: parseFloat(e.target.value) || 0 })}
                  className="w-full bg-white/10 text-white rounded-lg px-4 py-2 border border-white/20 focus:outline-none focus:border-purple-400"
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
              
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  {t('admin.bowlerDetail.maximumHandicap')}
                </label>
                <input
                  type="number"
                  value={adminSettings.hdcp_max}
                  onChange={(e) => setAdminSettings({ ...adminSettings, hdcp_max: parseInt(e.target.value) || 0 })}
                  className="w-full bg-white/10 text-white rounded-lg px-4 py-2 border border-white/20 focus:outline-none focus:border-purple-400"
                  min="0"
                  max="100"
                />
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => {
                  setShowSettingsModal(false);
                  fetchAdminSettings(); // Reset to saved values
                }}
                disabled={settingsLoading}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition duration-200 disabled:opacity-50"
              >
                {t('admin.bowlerDetail.cancel')}
              </button>
              <button
                onClick={() => updateAdminSettings(adminSettings)}
                disabled={settingsLoading}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition duration-200 disabled:opacity-50 flex items-center justify-center"
              >
                {settingsLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('admin.bowlerDetail.saving')}
                  </>
                ) : (
                  t('admin.bowlerDetail.saveSettings')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BowlerDetail;